import { CASES, OFFERS_FILTERS_STORAGE_KEY } from "./constants";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getStoredItemsPerPage,
  PAGINATION_STORAGE_KEYS,
  persistItemsPerPageFromPayload,
  withPreservedPagination,
} from "tap/utils/paginationPreferences.js";

const paginationKeys = {
  itemsPerPage: PAGINATION_STORAGE_KEYS.offers,
};
const initialItemsPerPage = getStoredItemsPerPage(paginationKeys.itemsPerPage);

/**
 * Offers Redux State Shape
 * @typedef {Object} OffersState
 * @property {Array} offers - List of offer records from API
 * @property {boolean} refreshOffers - Flag to trigger grid data refetch
 * @property {Object|null} selectedOffer - Currently selected offer for viewing/editing
 * @property {Object|null} selectedJob - Associated job for the selected offer
 * @property {Array} hiddenColumns - Column IDs hidden in the grid (user preference)
 * @property {Array} mediaOptions - Media options for filtering (from API)
 * @property {Array} territoryOptions - Territory options for filtering (from API)
 * @property {Array} languageOptions - Language options for filtering (from API)
 * @property {Array} formatOptions - Format options for filtering (from API)
 * @property {Array} currencyOptions - Currency options for filtering (from API)
 * @property {Array} transactionalWindowOptions - Transactional window options (from API)
 * @property {Object} internalFilters - Active filter values from filter drawer
 * @property {string} searchAll - Global search text
 * @property {Array} offerSubDubLanguageOptions - Sub/dub language options (from API)
 * @property {boolean} isOfferHistoryModalOpen - Offer history modal visibility
 * @property {Array} offerTableHistory - History records for selected offer
 * @property {boolean} isLoading - Data loading indicator
 * @property {boolean} isOffersUploadTasksDrawerOpen - Upload job tasks drawer (after file upload)
 * @property {Array} offersUploadJobTasks - Tasks from last offers upload job
 * @property {number} itemsPerPage - Number of items to display per page
 * @property {Array} pageSizeOptions - Available page size options for user selection
 */
const initialState = {
  offers: [],
  isOffersUploadTasksDrawerOpen: false,
  offersUploadJobTasks: [],
  refreshOffers: false,
  selectedOffer: null,
  selectedJob: null,
  hiddenColumns: ["createdAt", "licensingJobId", "message"],
  mediaOptions: [],
  territoryOptions: [],
  languageOptions: [],
  formatOptions: [],
  currencyOptions: [],
  transactionalWindowOptions: [],
  internalFilters: {},
  searchAll: "",
  offerSubDubLanguageOptions: [],
  isOfferHistoryModalOpen: false,
  offerTableHistory: [],
  isLoading: false,
  itemsPerPage: initialItemsPerPage,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
};

/** Helper to save filters to localStorage */
const saveFiltersToStorage = (filters, jobId) => {
  if (!jobId) return;
  try {
    localStorage.setItem(
      `${OFFERS_FILTERS_STORAGE_KEY}${jobId}`,
      JSON.stringify(filters),
    );
  } catch (e) {
    console.warn("Failed to save filters to localStorage:", e);
  }
};

export function offers(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case CASES.UPDATE_OFFERS: {
      persistItemsPerPageFromPayload(payload, paginationKeys);
      return { ...state, ...payload };
    }
    case CASES.UPDATE_OFFERS_FILTERS: {
      const { filters, jobId } = payload;
      const newFilters = { ...state.internalFilters, ...filters };
      saveFiltersToStorage(newFilters, jobId);
      return {
        ...state,
        internalFilters: newFilters,
      };
    }
    case CASES.SET_OFFERS_FILTERS: {
      const { filters, jobId } = payload;
      saveFiltersToStorage(filters, jobId);
      return {
        ...state,
        internalFilters: filters,
      };
    }
    case CASES.LOAD_OFFERS_FILTERS: {
      return {
        ...state,
        internalFilters: payload || {},
      };
    }
    case CASES.CLEAR_OFFERS: {
      return withPreservedPagination(state, {
        ...initialState,
        hiddenColumns: state.hiddenColumns,
      });
    }
    case CASES.CLEAR_OFFERS_FILTERS: {
      return {
        ...state,
        internalFilters: {},
        searchAll: "",
      };
    }
    default:
      return state;
  }
}
